import { type ReactElement } from "react";
import toast, { type ToastPosition } from "react-hot-toast";

import { Alert, type AlertProps, AlertType } from "./index";

interface ShowAlertOptions {
  duration?: number;
  position?: ToastPosition;
}

export interface AlertHook {
  dismiss: typeof toast.dismiss;
  remove: typeof toast.remove;
  showAlert: (
    props: Omit<AlertProps, "visible">,
    options?: ShowAlertOptions,
    type?: AlertType,
  ) => string;
  showAlertAsync: <T>(
    promise: Promise<T>,
    config: {
      error:
        | Omit<AlertProps, "id" | "visible">
        | ((error: Error) => Omit<AlertProps, "id" | "visible">);
      loading: Omit<AlertProps, "id" | "visible">;
      options?: ShowAlertOptions;
      success:
        | Omit<AlertProps, "id" | "visible">
        | ((data: T) => Omit<AlertProps, "id" | "visible">);
    },
  ) => Promise<T>;
}

export function useAlert(): AlertHook {
  const show = (
    props: Omit<AlertProps, "visible">,
    options: ShowAlertOptions = {},
  ): string => {
    const { duration = 5000, position = "top-center" } = options;

    return toast.custom(
      (t) => {
        const alertId = props.id || t.id;
        const alertElement: ReactElement = (
          <Alert
            {...props}
            id={alertId}
            visible={t.visible}
            onClose={() => toast.remove(alertId)}
          />
        );
        return alertElement;
      },
      {
        duration,
        id: props.id,
        position,
      },
    );
  };

  const showAlertAsync = async <T,>(
    promiseToWatch: Promise<T>,
    {
      loading,
      success,
      error,
      options = {},
    }: {
      error:
        | Omit<AlertProps, "id" | "visible">
        | ((error: Error) => Omit<AlertProps, "id" | "visible">);
      loading: Omit<AlertProps, "id" | "visible">;
      options?: ShowAlertOptions;
      success:
        | Omit<AlertProps, "id" | "visible">
        | ((data: T) => Omit<AlertProps, "id" | "visible">);
    },
  ): Promise<T> => {
    const loadingId = show(loading, { ...options, duration: Infinity });

    try {
      const data = await promiseToWatch;
      const successProps =
        typeof success === "function" ? success(data) : success;
      toast.remove(loadingId);
      show(successProps, { ...options, duration: 10000 });
      return data;
    } catch (err) {
      const errorProps =
        typeof error === "function" ? error(err as Error) : error;
      toast.remove(loadingId);
      show(errorProps, { ...options, duration: 10000 });
      throw err;
    }
  };

  return {
    dismiss: toast.dismiss,
    remove: toast.remove,
    showAlert: show,
    showAlertAsync,
  };
}
